import { BadgeColor, MUIBadge } from 'components/common/MUIBadge'
import { CampaignPostDTO, CampaignStatus, PostRole, PostStatus, SocialCampaignDTO, SocialNetwork } from 'models/socialCampaign'
import { CheckOutlined, ChevronRightOutlined, EditOutlined, ScheduleOutlined, SendOutlined, SmartDisplayOutlined } from '@mui/icons-material'
import { FC, ReactElement, memo } from 'react'
import { PostDisplayStatus, formatCampaignDay, postDisplayStatus } from 'utils/socialCampaign/campaignStatus'
import { coverOf, formatRowDate, postSlotOf, tiktokToConfirm, waitingKindOf } from 'utils/socialCampaign/schedule'

import { CONTENT_KIND_WAYS } from './contentKinds'
import { MediaThumb } from 'components/common/MediaThumb'
import { NetworkMark } from './NetworkMark.component'
import { ProductIcon } from 'components/common/ProductIcon'
import styles from './CampaignPostRow.module.sass'
import { useTranslation } from 'react-i18next'

/** The role's badge: blue opens the story, beige each highlight, coral each relaunch, gray the client's own post. */
export const ROLE_COLOR: Record<PostRole, BadgeColor> = {
  [PostRole.OVERVIEW]: 'blue',
  [PostRole.HIGHLIGHT]: 'beige',
  [PostRole.RELAUNCH]: 'coral',
  [PostRole.OWN]: 'gray',
}

export type BadgeStatus = Exclude<PostDisplayStatus, PostDisplayStatus.APPROVED>

/** Prototype v3 `STATUS`: colour and glyph of each status badge (Material outlined for the prototype's icons). */
export const STATUS_BADGE: Record<BadgeStatus, { color: BadgeColor, icon: ReactElement }> = {
  [PostDisplayStatus.DRAFT]: { color: 'orange', icon: <EditOutlined /> },
  [PostDisplayStatus.SCHEDULED]: { color: 'green', icon: <ScheduleOutlined /> },
  [PostDisplayStatus.HELD]: { color: 'gray', icon: <ScheduleOutlined /> },
  [PostDisplayStatus.PUBLISHED]: { color: 'blue', icon: <SendOutlined /> },
  [PostDisplayStatus.NOT_PUBLISHED]: { color: 'gray', icon: <ScheduleOutlined /> },
  [PostDisplayStatus.WAITING]: { color: 'gray', icon: <ScheduleOutlined /> },
}

/**
 * The badge a status shows. Once launched, an approved draft (one of "Generate new posts") reads Draft like the
 * others, as in the prototype: the review marks are a draft campaign's.
 */
export const badgeStatusOf = (status: PostDisplayStatus): BadgeStatus => status === PostDisplayStatus.APPROVED ? PostDisplayStatus.DRAFT : status

const Cover: FC<{ post: CampaignPostDTO }> = ({ post }) => {
  const { t } = useTranslation(['social_campaign'])
  const cover = coverOf(post)

  if (cover.kind === 'waiting') {
    const kind = waitingKindOf(post)
    return (
      <span className={styles.wait}>
        {kind ? <ProductIcon kind={CONTENT_KIND_WAYS[kind].icon} size="md" background="white" /> : <ScheduleOutlined sx={{ fontSize: '2rem' }} />}
      </span>
    )
  }

  // the backend signs no thumbnail for a video: its file's first frame
  if (cover.kind === 'video') {
    return <MediaThumb className={styles.thumb} kind="video" src={cover.url ? `${cover.url}#t=0.1` : undefined} alt="" kindIcon={<SmartDisplayOutlined />} />
  }

  return (
    <MediaThumb
      className={styles.thumb}
      kind={cover.kind === 'document' ? 'pdf' : 'photo'}
      src={cover.url ?? undefined}
      alt=""
      kindLabel={cover.kind === 'document' ? t('schedule.document') : undefined}
    />
  )
}

interface Props {
  post: CampaignPostDTO
  campaign: SocialCampaignDTO
  now: Date
  /**
   * Opens the post in the editor (ISSUE-3624): the row is then a button. Without it the row is static content: an
   * inert button would promise an editor that is not there yet.
   */
  onOpen?: (postId: string) => void
}

/**
 * One post of the schedule (prototype v3 `rowHtml`): the date column (day, weekday, time in the campaign's timezone),
 * the square cover, the title and its role badge, the network marks (TikTok's dotted while its settings are to
 * confirm), how many visuals, then the status. A draft campaign's drafts say "To review" or "Approved" instead of a
 * badge. On a phone the first line is the date and time with the status in full, then a 6.8rem cover beside the
 * title, its badge and the networks.
 */
export const CampaignPostRow = memo(function CampaignPostRow({ post, campaign, now, onOpen }: Props) {
  const { t, i18n } = useTranslation(['social_campaign'])
  const status = postDisplayStatus(post, campaign, now)
  const slot = postSlotOf(post, campaign.timezone)
  const date = formatRowDate(slot.day, i18n.language)
  const day = formatCampaignDay(slot.day, i18n.language)
  const reviewMark = campaign.status === CampaignStatus.DRAFT && post.status === PostStatus.DRAFT
  const waitingKind = status === PostDisplayStatus.WAITING ? waitingKindOf(post) : null
  const statusLabel = reviewMark && !post.reviewed ? t('schedule.to_review') : t(`schedule.status.${reviewMark ? status : badgeStatusOf(status)}`)
  const roleLabel = t(`schedule.role.${post.role}`)
  const badge = STATUS_BADGE[badgeStatusOf(status)]

  const note = status === PostDisplayStatus.WAITING
    ? (waitingKind ? t(`plan.kind.${waitingKind}`) : '')
    : t('schedule.visuals', { count: post.media.length })

  const networks = post.networks.map((network) => {
    const toConfirm = network === SocialNetwork.TIKTOK && tiktokToConfirm(post, status)
    return (
      <span key={network} className={styles.net} data-pending={toConfirm || undefined} title={toConfirm ? t('schedule.tiktok_to_confirm') : undefined}>
        <NetworkMark network={network} size={16} />
      </span>
    )
  })
  const networksSaid = post.networks.map((network) => network === SocialNetwork.TIKTOK && tiktokToConfirm(post, status)
    ? t('schedule.tiktok_to_confirm')
    : t(`network.${network}`)).join(', ')

  // the row may be a button, which holds phrasing content only: each badge renders as a span
  const statusContent = reviewMark
    ? post.reviewed
      ? <span className={styles.reviewed}><CheckOutlined sx={{ fontSize: '1.4rem' }} />{statusLabel}</span>
      : <span className={styles.toReview}>{statusLabel}</span>
    : <MUIBadge component="span" size="md" color={badge.color} icon={badge.icon} label={statusLabel} />

  const content = (
    <>
      {/* a phone's first line: when it goes out (the date column is a desktop's) */}
      <span className={styles.when}>{`${day} · ${slot.time}`}</span>
      <span className={styles.date}>
        <span className={styles.day}>{date.dayOfMonth}</span>
        <span className={styles.weekday}>{date.weekday}</span>
        <span className={styles.time}>{slot.time}</span>
      </span>
      <Cover post={post} />
      <span className={styles.main}>
        <span className={styles.title}>
          <span className={styles.subject}>{post.subject}</span>
          {' '}
          <MUIBadge component="span" size="sm" color={ROLE_COLOR[post.role]} className={styles.role} label={roleLabel} />
        </span>
        <span className={styles.meta}>
          <span className={styles.nets}>{networks}</span>
          {!onOpen && networksSaid && <span className={styles.srOnly}>{networksSaid}</span>}
          {note && <span className={styles.count}>{note}</span>}
        </span>
      </span>
      <span className={styles.status}>{statusContent}</span>
      {onOpen && <span className={styles.go} aria-hidden="true"><ChevronRightOutlined sx={{ fontSize: '1.6rem' }} /></span>}
    </>
  )

  return (
    <li>
      {onOpen
        ? (
          <button
            type="button"
            className={styles.row}
            data-post={post.id}
            data-status={status}
            aria-label={t('schedule.row_label', { role: roleLabel, subject: post.subject, date: day, time: slot.time, status: statusLabel })}
            onClick={() => onOpen(post.id)}
          >
            {content}
          </button>
        )
        : <div className={styles.row} data-post={post.id} data-status={status} data-static="">{content}</div>
      }
    </li>
  )
})
